import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  AutoComplete,
  message,
  Select,
  Row,
  Col,
  Card as AntCard,
  Upload,
  Progress,
  Space,
  Alert,
  Radio,
  Tag,
} from "antd";
import { CopyOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useQueryParam, StringParam } from "use-query-params";

const layout = {
  labelCol: {
    span: 8,
  },
  wrapperCol: {
    span: 16,
  },
};

/* eslint-disable no-template-curly-in-string */
const validateMessages = {
  required: "${label} è obbligatorio!",
  types: {
    email: "Inserisci un email valida",
  },
};

const success = () => {
  message.success("Card salvata con successo");
};

const error = (text = "Oops, si è verificato un errore") => {
  message.error(text);
};

const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/;

const imageContentTypes = ["image/jpeg", "image/png", "image/webp"];
const maxImageUploadSize = 5 * 1024 * 1024;
const descriptionMaxLength = 240;

const planOptions = [
  ["gratis", "Gratis"],
  ["plus", "Plus"],
];

const themeColorPattern = /^#[0-9a-fA-F]{6}$/;

const defaultThemeFields = {
  themePreset: "ofc-default",
  themePrimary: "#1b8395",
  themeSecondary: "#20acad",
  themeAccent: "#f9c534",
  themeBackground: "#f2f4f7",
  themeSurface: "#ffffff",
  themeText: "#111827",
  themeFont: "system",
  themeRadius: "rounded",
};

const themePresets = [
  {
    id: "ofc-default",
    name: "OFC default",
    fields: defaultThemeFields,
  },
  {
    id: "elegante-scura",
    name: "Elegante scura",
    fields: {
      themePreset: "elegante-scura",
      themePrimary: "#111827",
      themeSecondary: "#374151",
      themeAccent: "#d6a84f",
      themeBackground: "#f4f1ec",
      themeSurface: "#ffffff",
      themeText: "#111827",
      themeFont: "serif",
      themeRadius: "soft",
    },
  },
  {
    id: "chiara-professionale",
    name: "Chiara professionale",
    fields: {
      themePreset: "chiara-professionale",
      themePrimary: "#155e75",
      themeSecondary: "#0e7490",
      themeAccent: "#84cc16",
      themeBackground: "#eef6f8",
      themeSurface: "#ffffff",
      themeText: "#0f172a",
      themeFont: "system",
      themeRadius: "rounded",
    },
  },
  {
    id: "istituzionale",
    name: "Istituzionale",
    fields: {
      themePreset: "istituzionale",
      themePrimary: "#263b59",
      themeSecondary: "#496780",
      themeAccent: "#c99a3b",
      themeBackground: "#f4f6f8",
      themeSurface: "#ffffff",
      themeText: "#1f2937",
      themeFont: "classic",
      themeRadius: "square",
    },
  },
];

const themePresetOptions = [
  ...themePresets.map((preset) => [preset.id, preset.name]),
  ["custom", "Personalizzata"],
];

const themeFontOptions = [
  ["system", "Default / sans"],
  ["serif", "Serif elegante"],
  ["geometric", "Geometrico"],
  ["classic", "Classico"],
];

const themeRadiusOptions = [
  ["rounded", "Stondata"],
  ["soft", "Morbida"],
  ["square", "Squadrata"],
];

const themeColorFields = [
  ["themePrimary", "Primario"],
  ["themeSecondary", "Secondario"],
  ["themeAccent", "Accento"],
  ["themeBackground", "Sfondo pagina"],
  ["themeSurface", "Sfondo card"],
  ["themeText", "Testo"],
];

const planStatusOptions = [
  ["active", "Attivo"],
  ["coupon_lifetime", "Coupon lifetime"],
  ["inactive", "Non attivo"],
];

const isCouponRedeemable = (coupon) => {
  if (!coupon || coupon.status !== "active") {
    return false;
  }

  if (coupon.expiresAt && new Date(coupon.expiresAt).getTime() < Date.now()) {
    return false;
  }

  if (
    coupon.maxRedemptions &&
    (coupon.redeemedCount || 0) >= coupon.maxRedemptions
  ) {
    return false;
  }

  return true;
};

const getCouponOptionLabel = (coupon) =>
  `${coupon.code}${coupon.name ? ` · ${coupon.name}` : ""}${
    coupon.plan ? ` · ${coupon.plan}` : ""
  }`;

const sectionCardStyle = {
  marginBottom: 24,
};

const sectionBodyStyle = {
  paddingBottom: 4,
};

const descriptionHelpStyle = {
  display: "block",
  marginTop: 6,
  padding: "6px 8px",
  color: "#24415f",
  background: "#eef7f8",
  borderRadius: 4,
  fontSize: 12,
  lineHeight: "18px",
};

const getUploadValidationError = (file) => {
  if (!imageContentTypes.includes(file.type)) {
    return "Carica un'immagine JPG, PNG o WEBP.";
  }

  if (file.size > maxImageUploadSize) {
    return "L'immagine non può superare 5 MB.";
  }

  return "";
};

const setCardFieldValue = (form, fieldName, value) => {
  const currentCard = form.getFieldValue("card") || {};

  form.setFieldsValue({
    card: {
      ...currentCard,
      [fieldName]: value,
    },
  });
};

const getSignedUploadHeaderNames = (uploadUrl) => {
  try {
    const signedHeaders =
      new URL(uploadUrl).searchParams.get("X-Amz-SignedHeaders") || "";

    return new Set(
      signedHeaders
        .split(";")
        .map((headerName) => headerName.trim().toLowerCase())
        .filter(Boolean)
    );
  } catch {
    return new Set();
  }
};

const shouldSendUploadHeader = ({ name, signedHeaderNames }) => {
  const normalizedName = name.toLowerCase();

  return (
    !normalizedName.startsWith("x-amz-") ||
    signedHeaderNames.has(normalizedName)
  );
};

const uploadFileToPresignedUrl = ({ file, upload, onProgress }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const signedHeaderNames = getSignedUploadHeaderNames(upload.uploadUrl);

    request.open(upload.method || "PUT", upload.uploadUrl);

    Object.entries(upload.headers || {}).forEach(([name, value]) => {
      if (shouldSendUploadHeader({ name, signedHeaderNames })) {
        request.setRequestHeader(name, value);
      }
    });

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          percent: Math.round((event.loaded / event.total) * 100),
        });
      }
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }

      reject(new Error("Upload immagine non riuscito."));
    };

    request.onerror = () => {
      reject(new Error("Upload immagine non riuscito."));
    };

    request.send(file);
  });

const CardForm = (props) => {
  const [autoCompleteResult, setAutoCompleteResult] = useState([]);
  const [uploadState, setUploadState] = useState({});
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [aiCreditsMode, setAiCreditsMode] = useState("add");
  const [aiCreditsAmount, setAiCreditsAmount] = useState(1);
  const [aiCreditsRemaining, setAiCreditsRemaining] = useState(null);
  const [aiCreditsTotal, setAiCreditsTotal] = useState(null);
  const [aiCreditsBusy, setAiCreditsBusy] = useState(false);
  const [vatLookupBusy, setVatLookupBusy] = useState(false);
  const { isProd, isMobile } = useAppContext();
  const { getTokenSilently } = useAuth0();
  const env = isProd ? "prod" : "dev";

  const lookupVatAndPrefill = async () => {
    const rawVat = (form.getFieldValue(["card", "vat"]) || "").trim();
    if (!rawVat) {
      message.warning("Inserisci una Partita IVA prima di cercare.");
      return;
    }
    setVatLookupBusy(true);
    try {
      const token = await getTokenSilently();
      const res = await fetch(
        `${config.baseUrl[env]}/admin/billing/vat-lookup?vat=${encodeURIComponent(
          rawVat,
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await res.json();
      if (!res.ok) {
        message.error(
          data?.message === "INVALID_VAT"
            ? "Partita IVA non valida (servono 11 cifre)."
            : data?.message === "NOT_FOUND"
              ? "Nessuna azienda trovata per questa Partita IVA."
              : "Lookup non riuscito.",
        );
        return;
      }
      const c = data.company || {};
      const patch = {};
      const titleCase = (raw) =>
        String(raw || "")
          .split(/(\s+|[-/.,'])/)
          .map((piece) =>
            /^\s+$|^[-/.,']$/.test(piece)
              ? piece
              : piece.charAt(0).toUpperCase() + piece.slice(1).toLowerCase(),
          )
          .join("")
          .trim();
      if (c.ragioneSociale) {
        patch.billingCompanyName = c.ragioneSociale;
        patch.companyName = c.ragioneSociale;
      }
      if (c.via) {
        const formatted = titleCase(c.via);
        patch.billingAddress = formatted;
        patch.address = formatted;
      }
      if (c.citta) {
        const formatted = titleCase(c.citta);
        patch.billingCity = formatted;
        patch.city = formatted;
      }
      if (c.cap) {
        patch.billingZipCode = c.cap;
        patch.zipCode = c.cap;
      }
      patch.vat = c.vat || rawVat;
      if (c.codiceUnivocoSdi) patch.electronicInvoicingId = c.codiceUnivocoSdi;
      if (c.pec) patch.pec = c.pec;

      const current = form.getFieldValue("card") || {};
      form.setFieldsValue({ card: { ...current, ...patch } });
      message.success(
        data.source === "cache"
          ? "Dati azienda recuperati (cache)."
          : "Dati azienda recuperati.",
      );
    } catch (err) {
      message.error(`Lookup fallito: ${err.message}`);
    } finally {
      setVatLookupBusy(false);
    }
  };
  const history = useHistory();
  const location = useLocation();
  const { Option } = Select;
  const [form] = Form.useForm();

  const routeSlug = props.match?.params?.slug;
  const recordFromState =
    props.location.state || location.record || null;
  const recordSlug = recordFromState?.slug;

  useEffect(() => {
    if (recordSlug) {
      const expectedPath = `/cards/${recordSlug}`;
      if (props.location.pathname !== expectedPath) {
        history.replace({
          pathname: expectedPath,
          state: props.location.state,
          record: location.record,
        });
      }
      return;
    }

    if (routeSlug) {
      history.replace("/");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [companyName] = useQueryParam("companyName", StringParam);
  const [phone] = useQueryParam("phone", StringParam);
  const [firstName] = useQueryParam("firstName", StringParam);
  const [lastName] = useQueryParam("lastName", StringParam);
  const [mobile] = useQueryParam("mobile", StringParam);
  const [email] = useQueryParam("email", StringParam);
  const [companyBillingName] = useQueryParam("companyBillingName", StringParam);
  const [billingCompanyName] = useQueryParam("billingCompanyName", StringParam);
  const [website] = useQueryParam("website", StringParam);
  const [billingAddress] = useQueryParam("billingAddress", StringParam);
  const [pec] = useQueryParam("pec", StringParam);
  const [vat] = useQueryParam("vat", StringParam);
  const [electronicInvoicingId] = useQueryParam(
    "electronicInvoicingId",
    StringParam,
  );
  const [couponCode] = useQueryParam("couponCode", StringParam);
  const [address] = useQueryParam("address", StringParam);
  const [city] = useQueryParam("city", StringParam);

  // Pre-propulate card when copying
  let card = props.location.state || location.record;

  if (card?.companyBillingName && !card.billingCompanyName) {
    card.billingCompanyName = card.companyBillingName;
  }

  if (
    card === undefined ||
    card?.mobilePrefix === undefined ||
    card?.phonePrefix === undefined ||
    card?.isDisabled === undefined
  ) {
    card = {
      ...card,
      mobilePrefix: card?.mobilePrefix || "+39",
      phonePrefix: card?.phonePrefix || "+39",
      isDisabled: Boolean(card?.isDisabled),
    };
  }

  const isExistingDisabledCard = Boolean(card?.isDisabled);

  if (companyName) {
    card.companyName = companyName;
  }
  if (phone) {
    card.phone = phone;
  }
  if (firstName) {
    card.firstName = firstName;
  }
  if (lastName) {
    card.lastName = lastName;
  }
  if (mobile) {
    card.mobile = mobile;
  }
  if (email) {
    card.email = email;
  }
  if (companyBillingName) {
    card.billingCompanyName = companyBillingName;
  }
  if (billingCompanyName) {
    card.billingCompanyName = billingCompanyName;
  }
  if (website) {
    card.website = website;
  }
  if (billingAddress) {
    card.billingAddress = billingAddress;
  }
  if (pec) {
    card.pec = pec;
  }
  if (vat) {
    card.vat = vat;
  }
  if (electronicInvoicingId) {
    card.electronicInvoicingId = electronicInvoicingId;
  }
  if (couponCode) {
    card.couponCode = couponCode.trim().toUpperCase();
  }
  if (address) {
    card.address = address;
  }
  if (city) {
    card.city = city;
  }

  if (card.mobile && card.mobile.includes("+")) {
    card.mobilePrefix = card.mobile.slice(0, 3);
    card.mobile = card.mobile.slice(3);
  }

  if (card?.phone && card?.phone.includes("+")) {
    card.phonePrefix = card.phone.slice(0, 3);
    card.phone = card.phone.slice(3);
  }

  if (!location.record && !card.plan) {
    card.plan = "gratis";
    card.planStatus = "active";
  }

  if (card?.couponCode) {
    card.couponCode = card.couponCode.trim().toUpperCase();
  }

  card = {
    ...defaultThemeFields,
    ...card,
  };

  const onWebsiteChange = (value) => {
    if (!value) {
      setAutoCompleteResult([]);
    } else {
      setAutoCompleteResult(
        [".com", ".it", ".org", ".net"].map((domain) => `${value}${domain}`),
      );
    }
  };

  const websiteOptions = autoCompleteResult.map((website) => ({
    label: website,
    value: website,
  }));

  const copyCompanyDataToBilling = () => {
    const currentCard = form.getFieldValue("card") || {};
    const fieldsToCopy = [
      ["companyName", "billingCompanyName"],
      ["address", "billingAddress"],
      ["city", "billingCity"],
      ["zipCode", "billingZipCode"],
    ];
    const nextCardValues = fieldsToCopy.reduce((acc, [source, target]) => {
      const value = currentCard[source];

      if (typeof value === "string" ? value.trim() : value) {
        acc[target] = value;
      }

      return acc;
    }, {});

    if (!Object.keys(nextCardValues).length) {
      message.warning("Compila prima i dati aziendali da copiare.");
      return;
    }

    form.setFieldsValue({
      card: {
        ...currentCard,
        ...nextCardValues,
      },
    });
    message.success("Dati aziendali copiati nella fatturazione.");
  };

  const activeCoupons = useMemo(
    () => coupons.filter(isCouponRedeemable),
    [coupons],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchCoupons = async () => {
      setCouponsLoading(true);

      try {
        const token = await getTokenSilently();
        const response = await fetch(`${config.baseUrl[env]}/admin/coupons`, {
          headers: {
            "Content-type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(
            responseData.message || "Impossibile caricare i coupon",
          );
        }

        if (isMounted) {
          setCoupons(responseData.data || []);
        }
      } catch (err) {
        if (isMounted) {
          message.error(err.message);
        }
      } finally {
        if (isMounted) {
          setCouponsLoading(false);
        }
      }
    };

    fetchCoupons();

    return () => {
      isMounted = false;
    };
  }, [env, getTokenSilently]);

  useEffect(() => {
    setAiCreditsRemaining(
      typeof card?.aiCreditsRemaining === "number"
        ? card.aiCreditsRemaining
        : null,
    );
    setAiCreditsTotal(
      typeof card?.aiCreditsTotal === "number" ? card.aiCreditsTotal : null,
    );
  }, [card?.slug, card?.aiCreditsRemaining, card?.aiCreditsTotal]);

  const handleAiCreditsUpdate = async () => {
    if (!card?.slug) {
      message.error("Slug card non disponibile.");
      return;
    }
    if (typeof aiCreditsAmount !== "number" || Number.isNaN(aiCreditsAmount)) {
      message.error("Importo non valido.");
      return;
    }
    setAiCreditsBusy(true);
    try {
      const token = await getTokenSilently();
      const url = `${config.baseUrl[env]}/admin/cards/${encodeURIComponent(
        card.slug,
      )}/ai-credits`;
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mode: aiCreditsMode,
          amount: aiCreditsAmount,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Aggiornamento crediti AI non riuscito.");
      }
      setAiCreditsRemaining(data.aiCreditsRemaining ?? 0);
      setAiCreditsTotal(data.aiCreditsTotal ?? 0);
      message.success("Crediti AI aggiornati.");
    } catch (err) {
      message.error(err.message);
    } finally {
      setAiCreditsBusy(false);
    }
  };

  const prefixSelector = (prefix) => (
    <Form.Item name={["card", prefix]} noStyle>
      <Select style={{ width: 70 }}>
        <Option value="+39">+39</Option>
        <Option value="+44">+44</Option>
        <Option value="">No</Option>
      </Select>
    </Form.Item>
  );

  const applyThemePreset = (presetId) => {
    const preset = themePresets.find((item) => item.id === presetId);
    if (!preset) {
      return;
    }

    const currentCard = form.getFieldValue("card") || {};
    form.setFieldsValue({
      card: {
        ...currentCard,
        ...preset.fields,
      },
    });
  };

  const setThemeColorValue = (fieldName, value) => {
    const currentCard = form.getFieldValue("card") || {};
    form.setFieldsValue({
      card: {
        ...currentCard,
        themePreset: "custom",
        [fieldName]: value,
      },
    });
  };

  const requestUploadUrl = async ({ file, fieldName }) => {
    const token = await getTokenSilently();
    const response = await fetch(`${config.baseUrl[env]}/admin/upload-url`, {
      method: "POST",
      body: JSON.stringify({
        field: fieldName,
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        slug: location.record?.slug || card?.slug,
      }),
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(
        responseData.message || "Impossibile preparare l'upload immagine",
      );
    }

    return responseData;
  };

  const createImageUploadRequest =
    ({ fieldName, label }) =>
    async ({ file, onError, onProgress, onSuccess }) => {
      const validationError = getUploadValidationError(file);

      if (validationError) {
        message.error(validationError);
        onError(new Error(validationError));
        return;
      }

      setUploadState((current) => ({
        ...current,
        [fieldName]: {
          loading: true,
          percent: 0,
        },
      }));

      try {
        const upload = await requestUploadUrl({ file, fieldName });

        await uploadFileToPresignedUrl({
          file,
          upload,
          onProgress: (event) => {
            setUploadState((current) => ({
              ...current,
              [fieldName]: {
                loading: true,
                percent: event.percent,
              },
            }));
            onProgress(event);
          },
        });

        setCardFieldValue(form, fieldName, upload.assetUrl);
        setUploadState((current) => ({
          ...current,
          [fieldName]: {
            loading: false,
            percent: 100,
          },
        }));
        message.success(`${label} caricato correttamente.`);
        onSuccess(upload, file);
      } catch (err) {
        console.error(err);
        setUploadState((current) => ({
          ...current,
          [fieldName]: {
            loading: false,
            percent: 0,
          },
        }));
        message.error(err.message || "Upload immagine non riuscito.");
        onError(err);
      }
    };

  const renderImageUploadField = ({ fieldName, label, previewShape }) => {
    const state = uploadState[fieldName] || {};

    return (
      <Form.Item label={label}>
        <Form.Item name={["card", fieldName]} noStyle>
          <Input placeholder="URL immagine CDN" />
        </Form.Item>
        <Space style={{ marginTop: 8 }}>
          <Upload
            accept={imageContentTypes.join(",")}
            customRequest={createImageUploadRequest({ fieldName, label })}
            disabled={state.loading}
            showUploadList={false}
          >
            <Button
              htmlType="button"
              icon={<UploadOutlined />}
              loading={state.loading}
            >
              Carica
            </Button>
          </Upload>
          <Button
            disabled={state.loading}
            htmlType="button"
            icon={<DeleteOutlined />}
            onClick={() => setCardFieldValue(form, fieldName, "")}
          >
            Rimuovi
          </Button>
        </Space>
        {state.loading && (
          <Progress
            percent={state.percent}
            size="small"
            style={{ marginTop: 8 }}
          />
        )}
        <Form.Item
          noStyle
          shouldUpdate={(previous, current) =>
            previous?.card?.[fieldName] !== current?.card?.[fieldName]
          }
        >
          {({ getFieldValue }) => {
            const imageUrl = getFieldValue(["card", fieldName]);

            if (!imageUrl) {
              return null;
            }

            return (
              <div style={{ marginTop: 10 }}>
                <img
                  alt={label}
                  src={imageUrl}
                  style={{
                    width: previewShape === "avatar" ? 88 : 150,
                    height: previewShape === "avatar" ? 88 : 80,
                    objectFit: "cover",
                    borderRadius: previewShape === "avatar" ? "50%" : 6,
                    border: "1px solid #d9d9d9",
                    background: "#fafafa",
                  }}
                />
              </div>
            );
          }}
        </Form.Item>
      </Form.Item>
    );
  };

  const onSubmit = async ({ card }) => {
    const hide = message.loading("Salvataggio in corso...");
    const token = await getTokenSilently();
    const cardPayload = {
      ...card,
      couponCode: card.couponCode?.trim().toUpperCase() || "",
    };

    delete cardPayload.companyBillingName;

    let url = `${config.baseUrl[env]}/admin`;
    let httpMethod = "POST";

    if (location.record) {
      const slug = location.record.slug;
      url = `${url}?slug=${encodeURIComponent(slug)}`;
      httpMethod = "PATCH";
    }

    try {
      const response = await fetch(url, {
        method: httpMethod,
        body: JSON.stringify({ card: cardPayload, forceNotification: false }),
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const responseData = await response.json();

      if (!response.ok) {
        if (responseData.code === "DUPLICATE_CARD_SLUG") {
          const conflictingSlug = responseData.conflictingSlug;
          hide();
          error(
            <span>
              {responseData.message || "Slug già esistente."}
              {conflictingSlug && (
                <>
                  {" "}
                  <Link to={`/cards/${conflictingSlug}`}>
                    Apri la card con questo link
                  </Link>
                </>
              )}
            </span>,
          );
          return;
        }

        throw new Error(
          responseData.message || "Salvataggio card non riuscito",
        );
      }

      hide();
      success();
    } catch (err) {
      console.error(err);
      hide();
      error(err.message);
      return;
    }

    history.push("/");
  };

  return (
    <Form
      {...layout}
      name="nest-messages"
      form={form}
      onFinish={onSubmit}
      validateMessages={validateMessages}
      initialValues={{
        card,
      }}
    >
      {isExistingDisabledCard && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          message="Card disattivata"
          description="Il link pubblico non è raggiungibile e l'utente può accedere all'area personale solo per vedere l'avviso di contatto. Riattivala dal campo Stato pubblicazione o dalla lista card."
        />
      )}
      <Row gutter={24}>
        <Col span={isMobile ? 24 : 12}>
          <AntCard
            title="Dati personali"
            style={sectionCardStyle}
            bodyStyle={sectionBodyStyle}
          >
          {location.record && (
            <Form.Item
              name={["card", "slug"]}
              label="Slug pubblico"
              help="Modifica solo se devi forzare il link pubblico. Deve essere univoco."
              normalize={(value) => value?.toLowerCase()}
              rules={[
                {
                  pattern: slugPattern,
                  message:
                    "Usa lettere minuscole, numeri, trattini e, se serve, una barra tra impresa e persona.",
                },
              ]}
            >
              <Input placeholder="impresa/nome-cognome" />
            </Form.Item>
          )}
          <Form.Item
            name={["card", "firstName"]}
            label="Nome"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name={["card", "role"]}
            label="Ruolo"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={["card", "lastName"]}
            label="Cognome"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name={["card", "mobile"]} label="Cellulare">
            <Input addonBefore={prefixSelector("mobilePrefix")} />
          </Form.Item>
          <Form.Item
            name={["card", "email"]}
            label="Email personale"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Email personale obbligatoria",
              },
              {
                type: "email",
              },
            ]}
          >
            <Input />
          </Form.Item>
          {renderImageUploadField({
            fieldName: "avatarUrl",
            label: "Avatar / foto",
            previewShape: "avatar",
          })}
          </AntCard>
        </Col>

        <Col span={isMobile ? 24 : 12}>
          <AntCard
            title="Piano"
            style={sectionCardStyle}
            bodyStyle={sectionBodyStyle}
          >
          <Form.Item name={["card", "plan"]} label="Piano">
            <Select>
              {planOptions.map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={["card", "planStatus"]} label="Stato piano">
            <Select allowClear>
              {planStatusOptions.map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(previous, current) =>
              previous?.card?.couponCode !== current?.card?.couponCode
            }
          >
            {({ getFieldValue }) => {
              const currentCouponCode = getFieldValue(["card", "couponCode"]);
              const hasCurrentCoupon = Boolean(currentCouponCode);
              const hasCurrentCouponInActiveList = activeCoupons.some(
                (coupon) => coupon.code === currentCouponCode,
              );

              return (
                <Form.Item
                  name={["card", "couponCode"]}
                  label="Coupon"
                  help={
                    hasCurrentCoupon ? (
                      <Link
                        to={`/coupons?coupon=${encodeURIComponent(
                          currentCouponCode,
                        )}`}
                      >
                        Apri il coupon abbinato
                      </Link>
                    ) : (
                      "Seleziona solo coupon attivi e ancora riscattabili."
                    )
                  }
                >
                  <Select
                    allowClear
                    showSearch
                    loading={couponsLoading}
                    placeholder="Seleziona un coupon attivo"
                    optionFilterProp="children"
                  >
                    {hasCurrentCoupon && !hasCurrentCouponInActiveList && (
                      <Option value={currentCouponCode} disabled>
                        {currentCouponCode} · abbinato, non più attivo
                      </Option>
                    )}
                    {activeCoupons.map((coupon) => (
                      <Option key={coupon.code} value={coupon.code}>
                        {getCouponOptionLabel(coupon)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            name={["card", "isDisabled"]}
            label="Stato pubblicazione"
          >
            <Select>
              <Option value={false}>Attiva</Option>
              <Option value={true}>Disattivata</Option>
            </Select>
          </Form.Item>
          {card?.slug && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>
                Crediti AI Avatar
              </div>
              <Space size={8} wrap style={{ marginBottom: 12 }}>
                <Tag color="magenta">
                  Disponibili: {aiCreditsRemaining ?? "—"}
                </Tag>
                <Tag color="default">
                  Totale storico: {aiCreditsTotal ?? "—"}
                </Tag>
              </Space>
              <Space size={8} wrap>
                <Radio.Group
                  value={aiCreditsMode}
                  onChange={(e) => setAiCreditsMode(e.target.value)}
                >
                  <Radio.Button value="add">Aggiungi</Radio.Button>
                  <Radio.Button value="set">Imposta</Radio.Button>
                </Radio.Group>
                <InputNumber
                  value={aiCreditsAmount}
                  onChange={(value) => setAiCreditsAmount(value)}
                  step={1}
                  style={{ width: 120 }}
                  min={aiCreditsMode === "set" ? 0 : -100}
                />
                <Button
                  type="primary"
                  loading={aiCreditsBusy}
                  onClick={handleAiCreditsUpdate}
                >
                  Aggiorna
                </Button>
              </Space>
              <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
                Mode "Aggiungi" accetta valori negativi per scalare. Riservato al piano Plus.
              </div>
            </div>
          )}
          </AntCard>
        </Col>

        <Col span={24}>
          <Form.Item
            noStyle
            shouldUpdate={(previous, current) =>
              previous?.card?.plan !== current?.card?.plan ||
              previous?.card?.themePreset !== current?.card?.themePreset ||
              previous?.card?.themePrimary !== current?.card?.themePrimary ||
              previous?.card?.themeSecondary !== current?.card?.themeSecondary ||
              previous?.card?.themeAccent !== current?.card?.themeAccent ||
              previous?.card?.themeBackground !== current?.card?.themeBackground ||
              previous?.card?.themeSurface !== current?.card?.themeSurface ||
              previous?.card?.themeText !== current?.card?.themeText ||
              previous?.card?.themeRadius !== current?.card?.themeRadius ||
              previous?.card?.themeFont !== current?.card?.themeFont
            }
          >
            {({ getFieldValue }) => {
              const currentCard = {
                ...defaultThemeFields,
                ...(getFieldValue("card") || {}),
              };
              const isPlusCard = currentCard.plan === "plus";
              const selectedRadius =
                currentCard.themeRadius === "square"
                  ? 2
                  : currentCard.themeRadius === "soft"
                    ? 10
                    : 18;

              return (
                <AntCard
                  title={
                    <Space>
                      <span>Look & feel card</span>
                      <Tag color="cyan">Plus</Tag>
                    </Space>
                  }
                  style={sectionCardStyle}
                  bodyStyle={sectionBodyStyle}
                >
                  {!isPlusCard && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Personalizzazione disponibile solo per il piano Plus"
                      description="Puoi modificare questi campi dopo aver impostato la card su Plus. Anche se nel DB sono presenti valori tema, il frontend pubblico li ignora sulle card Gratis."
                    />
                  )}

                  <Row gutter={24}>
                    <Col span={isMobile ? 24 : 12}>
                      <Form.Item
                        name={["card", "themePreset"]}
                        label="Palette"
                      >
                        <Select
                          disabled={!isPlusCard}
                          onChange={(value) => {
                            if (value !== "custom") {
                              applyThemePreset(value);
                            }
                          }}
                        >
                          {themePresetOptions.map(([value, label]) => (
                            <Option key={value} value={value}>
                              {label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item name={["card", "themeFont"]} label="Font">
                        <Select disabled={!isPlusCard}>
                          {themeFontOptions.map(([value, label]) => (
                            <Option key={value} value={value}>
                              {label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name={["card", "themeRadius"]}
                        label="Stile card"
                      >
                        <Select disabled={!isPlusCard}>
                          {themeRadiusOptions.map(([value, label]) => (
                            <Option key={value} value={value}>
                              {label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Button
                        htmlType="button"
                        disabled={!isPlusCard}
                        onClick={() => applyThemePreset("ofc-default")}
                      >
                        Ripristina default OFC
                      </Button>
                    </Col>

                    <Col span={isMobile ? 24 : 12}>
                      <Row gutter={12}>
                        {themeColorFields.map(([fieldName, label]) => (
                          <Col key={fieldName} span={isMobile ? 24 : 12}>
                            <Form.Item
                              name={["card", fieldName]}
                              label={label}
                              rules={[
                                {
                                  pattern: themeColorPattern,
                                  message: "Usa un colore HEX valido.",
                                },
                              ]}
                            >
                              <Input
                                type="color"
                                disabled={!isPlusCard}
                                onChange={(event) =>
                                  setThemeColorValue(
                                    fieldName,
                                    event.target.value,
                                  )
                                }
                              />
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>

                  <div
                    style={{
                      marginTop: 12,
                      padding: 16,
                      borderRadius: selectedRadius,
                      background: currentCard.themeSurface,
                      color: currentCard.themeText,
                      border: "1px solid #f0f0f0",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        margin: -16,
                        marginBottom: 16,
                        padding: "18px 16px",
                        background: `linear-gradient(135deg, ${currentCard.themePrimary}, ${currentCard.themeSecondary})`,
                        color: "#fff",
                      }}
                    >
                      <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {currentCard.firstName || "Nome"}{" "}
                        {currentCard.lastName || "Cognome"}
                      </div>
                      <div style={{ opacity: 0.85 }}>
                        {currentCard.role || "Ruolo professionale"}
                      </div>
                    </div>
                    <Space wrap>
                      {themeColorFields.map(([fieldName, label]) => (
                        <Tag
                          key={fieldName}
                          color={currentCard[fieldName]}
                          style={{ color: fieldName === "themeText" ? "#fff" : undefined }}
                        >
                          {label}: {currentCard[fieldName]}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                </AntCard>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={24}>
          <AntCard
            title="Dati aziendali"
            style={sectionCardStyle}
            bodyStyle={sectionBodyStyle}
          >
            <Row gutter={24}>
              <Col span={isMobile ? 24 : 12}>
          <Form.Item
            name={["card", "companyName"]}
            label="Nome Impresa"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          {renderImageUploadField({
            fieldName: "logoUrl",
            label: "Logo azienda",
          })}
          <Form.Item
            name={["card", "description"]}
            label="Descrizione"
            extra={
              <span style={descriptionHelpStyle}>
                Massimo {descriptionMaxLength} caratteri. Usa un testo breve, 3
                o 4 righe al massimo.
              </span>
            }
          >
            <Input.TextArea
              rows={4}
              maxLength={descriptionMaxLength}
              showCount
            />
          </Form.Item>
          <Form.Item name={["card", "phone"]} label="Telefono ufficio">
            <Input addonBefore={prefixSelector("phonePrefix")} />
          </Form.Item>
          <Form.Item
            name={["card", "companyEmail"]}
            label="Email ufficio"
            rules={[
              {
                type: "email",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name={["card", "website"]} label="Sito Web">
            <AutoComplete
              options={websiteOptions}
              onChange={onWebsiteChange}
              placeholder="Website"
            >
              <Input />
            </AutoComplete>
          </Form.Item>
          <Form.Item name={["card", "address"]} label="Indirizzo">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "city"]} label="Città">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "zipCode"]} label="CAP">
            <Input />
          </Form.Item>
              </Col>
              <Col span={isMobile ? 24 : 12}>
          <Form.Item name={["card", "linkedin"]} label="LinkedIn">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "facebook"]} label="Facebook">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "instagram"]} label="Instagram">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "twitter"]} label="X / Twitter">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "youtube"]} label="YouTube">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "tiktok"]} label="TikTok">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "videoUrl"]} label="Video URL">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "videoTitle"]} label="Titolo video">
            <Input />
          </Form.Item>
              </Col>
            </Row>
          </AntCard>
        </Col>

        <Col span={24}>
          <AntCard
            title="Dati di fatturazione"
            style={sectionCardStyle}
            bodyStyle={sectionBodyStyle}
          >
            <Button
              htmlType="button"
              icon={<CopyOutlined />}
              onClick={copyCompanyDataToBilling}
              style={{ marginBottom: 16 }}
            >
              Copia da dati aziendali
            </Button>
            <Row gutter={24}>
              <Col span={isMobile ? 24 : 12}>
          <Form.Item
            name={["card", "billingCompanyName"]}
            label="Ragione Sociale"
          >
            <Input />
          </Form.Item>
          <Form.Item name={["card", "billingAddress"]} label="Indirizzo">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "billingCity"]} label="Città">
            <Input />
          </Form.Item>
          <Form.Item name={["card", "billingZipCode"]} label="CAP">
            <Input />
          </Form.Item>
              </Col>
              <Col span={isMobile ? 24 : 12}>
          <Form.Item name={["card", "vat"]} label="Partita IVA">
            <Input.Search
              enterButton="Cerca"
              loading={vatLookupBusy}
              onSearch={lookupVatAndPrefill}
              placeholder="11 cifre, opzionale prefisso IT"
            />
          </Form.Item>
          <Form.Item name={["card", "pec"]} label="Pec">
            <Input />
          </Form.Item>
          <Form.Item
            name={["card", "electronicInvoicingId"]}
            label="Codice Destinatario"
          >
            <Input />
          </Form.Item>
              </Col>
            </Row>
          </AntCard>
        </Col>

        <Col span={24}>
          <Form.Item style={{ textAlign: "right" }}>
            <Button
              type="primary"
              htmlType="submit"
              style={{ marginRight: "25px" }}
            >
              {location.record ? "Aggiorna Card" : "Crea Card"}
            </Button>
            <Link to="/">
              <Button danger>Annulla</Button>
            </Link>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default CardForm;
